import { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useTranslation } from "react-i18next";
import { useCustomColors } from "../../hooks/useCustomColors";
import { GAME_ROOMS } from "../../utils/games";
import { CARD_PADDING, GRID_GAP } from "../../utils/spacing";
import { GameHallItem, getGames } from "../../api";

interface AuthGameHallPreviewProps {
  onExplore?: () => void;
}

const AuthGameHallPreview = ({ onExplore }: AuthGameHallPreviewProps) => {
  const colors = useCustomColors();
  const { t } = useTranslation();
  const [games, setGames] = useState<GameHallItem[]>(GAME_ROOMS);

  useEffect(() => {
    let cancelled = false;

    getGames()
      .then((response) => {
        if (!cancelled && response.status === 1) setGames(response.items);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ p: CARD_PADDING, borderRadius: "20px", background: colors.card.gradient, border: `1px solid ${colors.border.accent}`, boxShadow: colors.card.shadow }}>
      <Typography sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: "3px", color: colors.accent.gold }}>{t("auth_preview_game_hall")}</Typography>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: "6px", gap: "12px" }}>
        <Typography sx={{ fontSize: { xs: "20px", md: "24px" }, fontWeight: 800, color: colors.text.primary }}>{t("auth_preview_choose_game")}</Typography>
        <ArrowForwardRoundedIcon sx={{ color: colors.accent.gold }} />
      </Stack>
      <Box
        sx={{
          mt: "20px",
          display: "grid",
          // 2-up through tablet (<=1024px); 3-up only on desktop.
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: GRID_GAP,
          "@media (min-width:1025px)": {
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {games.map((game) => (
          <Box key={game.code} onClick={onExplore} sx={{ position: "relative", minHeight: { xs: "120px", md: "190px" }, p: { xs: "14px", md: "18px" }, display: "flex", flexDirection: "column", justifyContent: "flex-end", cursor: onExplore ? "pointer" : "default", borderRadius: "14px", overflow: "hidden", backgroundImage: `linear-gradient(0deg, ${colors.tint.imageScrim} 0%, rgba(0, 0, 0, 0.25) 72%), url(${game.image})`, backgroundSize: "cover", backgroundPosition: "center", border: `1px solid ${colors.border.accent}`, "&:hover": onExplore ? { borderColor: colors.accent.gold } : undefined }}>
            <Box className="primary-shining-pill" sx={{ position: "absolute", top: "10px", left: "10px", height: "18px", px: "8px", display: "flex", alignItems: "center", borderRadius: "9px", fontSize: "9px", fontWeight: 800 }}>{game.badge || t(game.badgeKey || "common_status")}</Box>
            <Typography sx={{ fontSize: { xs: "13px", md: "16px" }, fontWeight: 800, color: "#FFFFFF" }}>{game.name || t(game.nameKey || "common_status")}</Typography>
            <Typography sx={{ mt: "4px", fontSize: "10px", color: colors.text.secondary }}>{game.subtitle || t(game.detailKey || "common_status")}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AuthGameHallPreview;
